import { useEffect, useState } from 'react';
import { draftField, useDraft } from '../../offline/draft.js';
import { FileText, Lock } from 'lucide-react';
import {
  TICKET_CATEGORY_LABEL, TICKET_PRIORITIES, TICKET_PRIORITY_LABEL, TICKET_STATUSES, formatDateTime, ticketStatusLabel,
} from '@nu/shared';
import { api } from '../../api/client.js';
import PhotoPicker from '../../components/PhotoPicker.jsx';
import { replyToTicket, updateTicket, useTicket } from './store.js';

const STATUS_CLASS = { new: 'status-assigned', in_progress: 'status-in_progress', waiting_on_user: 'status-paused', resolved: 'status-completed' };
export function TicketStatus({ status, raiser = false }) {
  return <span className={`status ${STATUS_CLASS[status] || ''}`}>{ticketStatusLabel(status, { raiser })}</span>;
}

// One ticket: the conversation, its files, and a reply box. The helpdesk
// also gets internal notes, status/priority/assignee, the device facts and
// what happened to it. Used as a page (field) and in a pop-up (office).
export default function TicketThread({ id, me, agent = false }) {
  const { ticket: t, status, pendingFiles } = useTicket(id);
  if (!t) return <p className="muted">{status === 'missing' ? 'This ticket isn’t here, or isn’t yours.' : status === 'offline' ? 'This ticket can’t be loaded with no signal.' : 'Loading…'}</p>;
  const raiser = t.raisedById === me.id;
  const working = agent && !raiser;

  return (
    <div className="ticket">
      <div className="ticket-head">
        <p className="small muted">{t.reference || 'Sending…'} · {TICKET_CATEGORY_LABEL[t.category]}</p>
        <h2 className="ticket-title">{t.subject}</h2>
        <p className="ticket-tags">
          <TicketStatus status={t.status} raiser={raiser} />
          {(t.priority === 'high' || t.priority === 'urgent') && <span className="status status-aborted">{TICKET_PRIORITY_LABEL[t.priority]}</span>}
          <span className="small muted">
            {raiser ? 'You raised it' : `Raised by ${t.raisedByName}`} {formatDateTime(t.raisedAt)}
            {t.assignedToName ? ` · With ${t.assignedToName}` : ''}
          </span>
        </p>
        {t.local && !t.reference && <p className="notice-line notice-warn">Saved on this device. It sends when you have signal.</p>}
        {status === 'offline' && !t.local && <p className="small muted">No signal: showing the last copy on this device.</p>}
      </div>

      {working && <AgentControls t={t} me={me} />}

      <ol className="ticket-msgs">
        {t.messages.map((m) => <Message key={m.id} m={m} mine={m.authorId === me.id} files={(t.files || []).filter((f) => f.messageId === m.id)} ticketId={t.id} />)}
      </ol>
      <Files ticketId={t.id} files={(t.files || []).filter((f) => !f.messageId || !t.messages.some((m) => m.id === f.messageId))} />
      {pendingFiles > 0 && <p className="small muted">{pendingFiles === 1 ? '1 photo' : `${pendingFiles} photos`} waiting to upload.</p>}

      <Reply t={t} me={me} agent={working} raiser={raiser} />

      {working && <AgentDetails t={t} />}
    </div>
  );
}

function Message({ m, mine, files, ticketId }) {
  const who = m.internal ? 'note' : mine ? 'mine' : m.side === 'agent' ? 'agent' : 'raiser';
  return (
    <li className={`ticket-msg is-${who}`}>
      <p className="ticket-msg-head small">
        {m.internal && <Lock size={14} aria-hidden="true" />}
        <strong>{mine ? 'You' : m.authorName}</strong>
        {m.side === 'agent' && !mine && !m.internal && <span className="muted"> · Helpdesk</span>}
        {m.internal && <span> · Internal note (only the helpdesk sees this)</span>}
        <span className="muted"> · {m.local ? 'Sending…' : formatDateTime(m.sentAt)}</span>
      </p>
      <p className="ticket-msg-body">{m.body}</p>
      <Files ticketId={ticketId} files={files} />
    </li>
  );
}

// Thumbnails for pictures (tap for full size); PDFs as a link.
function Files({ ticketId, files }) {
  const [urls, setUrls] = useState({});
  const [big, setBig] = useState(null);
  const key = files.map((f) => f.id).join(',');
  useEffect(() => {
    let live = true;
    const made = [];
    (async () => {
      for (const f of files) {
        const blob = await api(`/helpdesk/tickets/${ticketId}/files/${f.id}`, { as: 'blob' }).catch(() => null);
        if (!live || !blob) continue;
        const url = URL.createObjectURL(blob);
        made.push(url);
        setUrls((u) => ({ ...u, [f.id]: url }));
      }
    })();
    return () => { live = false; made.forEach((u) => URL.revokeObjectURL(u)); setUrls({}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, key]);
  if (!files.length) return null;
  return (
    <>
      <ul className="photo-picks ticket-files">
        {files.map((f) => (
          <li key={f.id}>
            {f.contentType === 'application/pdf'
              ? <a className="ticket-pdf" href={urls[f.id]} target="_blank" rel="noreferrer"><FileText size={22} aria-hidden="true" /><span className="small">{f.name}</span></a>
              : urls[f.id]
                ? <button type="button" className="photo-open" onClick={() => setBig(f)} aria-label={`Open ${f.name}`}><img src={urls[f.id]} alt={f.name} /></button>
                : <span className="ticket-file-wait small muted">Loading…</span>}
          </li>
        ))}
      </ul>
      {big && (
        <div className="popup-backdrop" onMouseDown={() => setBig(null)}>
          <figure className="photo-big">
            <img src={urls[big.id]} alt={big.name} />
            <figcaption>{big.name} · {formatDateTime(big.createdAt)} · {big.uploadedByEmail}</figcaption>
            <button type="button" className="btn popup-btn" onClick={() => setBig(null)}>Close</button>
          </figure>
        </div>
      )}
    </>
  );
}

function Reply({ t, me, agent, raiser }) {
  // The reply being typed is kept on the phone until it's sent.
  const [draft, saveDraft, clearDraft] = useDraft(`nu.draft.reply.${t.id}`);
  const [body, setBody] = draftField(draft, saveDraft, 'body', '');
  const [internal, setInternal] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [adding, setAdding] = useState(false);
  const [key, setKey] = useState(0);
  const send = async (e) => {
    e.preventDefault();
    if (body.trim().length < 2 || adding) return;
    await replyToTicket(me, t.id, { body: body.trim(), internal: agent && internal }, photos.map((p, i) => ({ blob: p.blob, name: `photo-${i + 1}.jpg` })));
    clearDraft();
    setPhotos([]);
    setKey((k) => k + 1); // a fresh photo picker
  };
  return (
    <form className={`ticket-reply${internal ? ' is-note' : ''}`} onSubmit={send}>
      <label className="fld">
        {internal ? 'Internal note' : 'Reply'}
        <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000} placeholder={internal ? 'Only the helpdesk sees this' : agent ? `Reply to ${t.raisedByName}` : 'Write to the helpdesk'} />
      </label>
      <PhotoPicker key={key} photos={photos} setPhotos={setPhotos} onBusy={setAdding} label="Photos or screenshots" max={4} />
      {raiser && t.status === 'resolved' && <p className="small muted">This ticket is resolved. Replying opens it again.</p>}
      <div className="actions">
        {agent && (
          <label className="check small">
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Internal note
          </label>
        )}
        <button type="submit" className="btn primary" disabled={body.trim().length < 2 || adding}>{internal ? 'Add note' : 'Send'}</button>
      </div>
    </form>
  );
}

// Helpdesk: status, priority and who has it. Each change is queued like any
// other write and shows when the ticket next reloads.
function AgentControls({ t, me }) {
  const [team, setTeam] = useState([]);
  useEffect(() => { api('/helpdesk/agents').then(setTeam, () => setTeam([])); }, []);
  const set = (patch) => updateTicket(t.id, patch);
  const people = team.some((p) => p.id === t.assignedToId) || !t.assignedToId ? team : [...team, { id: t.assignedToId, name: t.assignedToName }];
  return (
    <div className="ticket-controls">
      <label className="fld narrow">
        Status
        <select value={t.status} onChange={(e) => set({ status: e.target.value })}>
          {TICKET_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </label>
      <label className="fld narrow">
        Priority
        <select value={t.priority} onChange={(e) => set({ priority: e.target.value })}>
          {TICKET_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </label>
      <label className="fld">
        Assigned to
        <select value={t.assignedToId || ''} onChange={(e) => { const p = people.find((x) => x.id === e.target.value); set({ assignee: p ? { id: p.id, name: p.name } : null }); }}>
          <option value="">Nobody</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.id === me.id ? ' (me)' : ''}</option>)}
        </select>
      </label>
      <div className="ticket-quick">
        {t.assignedToId !== me.id && <button type="button" className="btn" onClick={() => set({ assignee: { id: me.id, name: me.name }, ...(t.status === 'new' ? { status: 'in_progress' } : {}) })}>Take it</button>}
        {t.status !== 'waiting_on_user' && t.status !== 'resolved' && <button type="button" className="btn" onClick={() => set({ status: 'waiting_on_user' })}>Waiting on user</button>}
        {t.status !== 'resolved' && <button type="button" className="btn primary" onClick={() => set({ status: 'resolved' })}>Resolve</button>}
      </div>
    </div>
  );
}

const DEVICE_LABEL = { app: 'Version', platform: 'Using', view: 'View', online: 'Online', waitingToSend: 'Waiting to send', needAttention: 'Need attention', notifications: 'Notifications', browser: 'Browser' };
function AgentDetails({ t }) {
  const device = t.device ? Object.entries(t.device) : [];
  return (
    <details className="ticket-more">
      <summary>Device and history</summary>
      {device.length > 0 && (
        <dl className="facts">
          {device.map(([k, v]) => [<dt key={`${k}t`}>{DEVICE_LABEL[k] || k}</dt>, <dd key={`${k}d`} className="ticket-device-val">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</dd>])}
        </dl>
      )}
      <ul className="history">
        {(t.events || []).map((e, i) => (
          <li key={i}><span>{e.summary}</span><span className="small muted">{formatDateTime(e.at)} · {e.actorEmail}</span></li>
        ))}
      </ul>
    </details>
  );
}
